// Helper function to convert currency code to symbol
const getCurrencySymbol = (currencyCode) => {
  const symbols = {
    'USD': '$',
    'GBP': '£',
    'AUD': 'A$',
  };
  return symbols[currencyCode] || '';
};

// Get currency symbol based on account ID (for columns files that use acc.id)
const getCurrencySymbolByAccountId = (accountId, user, returnEmptyForAggregate = true) => {
  if (!user || !user.capitalAccounts) {
    return returnEmptyForAggregate ? '' : '$';
  }

  const capitalAccounts = user.capitalAccounts || [];

  // For specific account IDs, find the account and use its currency
  if (
    accountId &&
    accountId !== "All" &&
    accountId !== "All Personal" &&
    accountId !== "All Investor"
  ) {
    // Try both _id and id for compatibility
    const account = capitalAccounts.find((acc) => acc._id === accountId || acc.id === accountId);
    if (account?.currency) {
      return getCurrencySymbol(account.currency);
    }
  }

  // For aggregate views, return empty string (no currency symbol)
  if (returnEmptyForAggregate && (
    accountId === "All" ||
    accountId === "All Personal" ||
    accountId === "All Investor"
  )) {
    return "";
  }

  // Fallback based on equity type
  return "$";
};

// Get currency symbol based on account context (for journal, etc.)
const getCurrencyForAccount = (accountId, user) => {
  if (!user || !user.capitalAccounts) {
    return '$';
  }

  // Check if the account has a specific currency
  if (accountId) {
    const account = user.capitalAccounts.find(acc => acc._id === accountId || acc.id === accountId);
    if (account?.currency) {
      return getCurrencySymbol(account.currency);
    }
  }

  // Fallback to user's first active account currency
  const allAccounts = user.capitalAccounts.filter(acc => acc.isActive);
  if (allAccounts.length > 0 && allAccounts[0].currency) {
    return getCurrencySymbol(allAccounts[0].currency);
  }

  // Final fallback
  return '$';
};

// Get currency symbol for selected account context (for statsbar, new-reflection, single-reflections)
const getCurrencyForSelectedAccount = (selectedAccountId, selectedAccount, user, returnEmptyForAggregate = true) => {
  if (!user) {
    return returnEmptyForAggregate ? '' : '$';
  }

  // For aggregate views, return empty string (no currency symbol)
  if (returnEmptyForAggregate && (
    selectedAccountId === "All" ||
    selectedAccountId === "All Personal" ||
    selectedAccountId === "All Investor"
  )) {
    return "";
  }

  // First check if the selected account has a specific currency
  if (selectedAccount?.currency) {
    return getCurrencySymbol(selectedAccount.currency);
  }

  // Determine account type from selectedAccountId
  const accountType = selectedAccountId?.startsWith("pc-") ? "Personal" :
                      selectedAccountId?.startsWith("ic-") ? "Investor" : null;

  // For aggregate views or account type detection
  if (selectedAccountId === "All Personal" || accountType === "Personal") {
    const personalAccounts = user.capitalAccounts?.filter(acc => acc.type === "Personal" && acc.isActive);
    if (personalAccounts?.length > 0 && personalAccounts[0].currency) {
      return getCurrencySymbol(personalAccounts[0].currency);
    }
    // Fallback to user's default currency
    const userCurrency = user.defaultCurrency || 'GBP';
    return getCurrencySymbol(userCurrency);
  } else if (selectedAccountId === "All Investor" || accountType === "Investor") {
    const investorAccounts = user.capitalAccounts?.filter(acc => acc.type === "Investor" && acc.isActive);
    if (investorAccounts?.length > 0 && investorAccounts[0].currency) {
      return getCurrencySymbol(investorAccounts[0].currency);
    }
    // Fallback to user's default currency
    const userCurrency = user.defaultCurrency || 'USD';
    return getCurrencySymbol(userCurrency);
  } else if (selectedAccountId === "All") {
    const allAccounts = user.capitalAccounts?.filter(acc => acc.isActive);
    if (allAccounts?.length > 0 && allAccounts[0].currency) {
      return getCurrencySymbol(allAccounts[0].currency);
    }
    return '';
  }

  // Final fallback
  return '$';
};

// Get currency for a specific row (for columns files)
const getRowCurrency = (row, selectedAccountId, user) => {
  // First try to get currency from the row's accountId
  if (row.accountId) {
    const symbol = getCurrencySymbolByAccountId(row.accountId, user, false);
    if (symbol) return symbol;
  }

  // Fallback to equity type
  if (row.equityType === "Personal Capital") {
    return getCurrencySymbolByAccountId(selectedAccountId, user, false) || "£";
  } else if (
    row.equityType === "Backtest" ||
    row.equityType === "Investor Capital"
  ) {
    return getCurrencySymbolByAccountId(selectedAccountId, user, false) || "$";
  }

  return "$";
};

export { getCurrencySymbol, getCurrencySymbolByAccountId, getCurrencyForAccount, getCurrencyForSelectedAccount, getRowCurrency };
